<template>
  <div class="leftovers-groups">
    <div class="leftovers-list">
      <div v-for="(group, index) in leftovers" :key="index" class="group-item">
        <div class="group-field">
          <span class="group-field-title">{{ 'base.date' | translate }}</span>
          <span>{{ group.date_leftover }}</span>
        </div>
        <div class="group-field">
          <span class="group-field-title">{{ 'base.animal_group' | translate }}</span>
          <span class="group-field-value">
              <span @click="openLeftoversInfoDialog(group)" class="info-icon i-util-i mr-2"></span>
            {{ group.group.name }}
          </span>
        </div>
        <div class="group-field">
          <span class="group-field-title">{{ 'base.animal_head' | translate }}</span>
          <span>{{ group.animal_count }}</span>
        </div>
        <div class="group-field">
          <span class="group-field-title">{{ 'base.weight' | translate }}</span>
          <span>{{ formatWeight(group.fact_weight) }}</span>
        </div>
        <div class="group-field">
          <span class="group-field-title">{{ 'base.leftovers' | translate }} [{{ 'base.kg' | translate }}]</span>
          <input type="number" class="input" :value="group.fact_leftover" @blur="changeFactLeftOver($event, group)">
        </div>
        <div class="group-field">
          <span class="group-field-title">{{ 'base.leftovers_percent' | translate }}</span>
          <span>{{ formatPercentage(leftoversPercentage(group)) }}%</span>
        </div>
        <div class="group-field">
          <span class="group-field-title">{{ 'base.leftovers_feeding' | translate }}</span>
          <span>{{ formatWeight(feedWeightPerHead(group)) }}</span>
        </div>
        <div class="group-field">
          <span class="group-field-title">{{ 'base.leftovers_feeding_fact' | translate }}</span>
          <span>{{ formatWeight(actualWeightPerHead(group)) }}</span>
        </div>
      </div>

      <!-- TOTALS BLOCK -->
      <div v-if="!isListEmpty" class="group-item totals-item">
        <div class="group-field">
          <span class="group-field-title">{{ 'base.animal_group' | translate }}</span>
          <span class="group-field-value-total">
            <strong>{{ 'base.total' | translate }} ({{ totals.groupCount }})</strong>
          </span>
        </div>
        <div class="group-field">
          <span class="group-field-title">{{ 'base.animal_head' | translate }}</span>
          <span class="group-field-value-total">
            <strong>{{ totals.animalCount }}</strong>
          </span>
        </div>
        <div class="group-field">
          <span class="group-field-title">{{ 'base.weight' | translate }}</span>
          <span class="group-field-value-total">
            <strong>{{ formatWeight(totals.totalWeight) }}</strong>
          </span>
        </div>
        <div class="group-field">
          <span class="group-field-title">{{ 'base.leftovers' | translate }} [{{ 'base.kg' | translate }}]</span>
          <span class="group-field-value-total">
            <strong>{{ formatWeight(totals.totalLeftovers) }}</strong>
          </span>
        </div>
        <div class="group-field">
          <span class="group-field-title">{{ 'base.leftovers_percent' | translate }}</span>
          <span class="group-field-value-total">
            <strong>{{ formatPercentage(totals.totalLeftoversPercent) }}%</strong>
          </span>
        </div>
        <div class="group-field">
          <span class="group-field-title">{{ 'base.leftovers_feeding' | translate }}</span>
          <span class="group-field-value-total">
            <strong>{{ formatWeight(totals.avgFeedWeightPerHead) }}</strong>
          </span>
        </div>
        <div class="group-field">
          <span class="group-field-title">{{ 'base.leftovers_feeding_fact' | translate }}</span>
          <span class="group-field-value-total">
            <strong>{{ formatWeight(totals.avgActualWeightPerHead) }}</strong>
          </span>
        </div>
      </div>

      <div v-if="loaded && isListEmpty" class="no-data">
        {{ 'base.no_data_available' | translate }}
      </div>
    </div>

    <v-dialog max-width="660" v-model="showLeftoversInfoDialog" @close="closeLeftoversInfoDialog">
      <leftovers-info-dialog :data="activeItem" @close="closeLeftoversInfoDialog" />
    </v-dialog>
  </div>
</template>

<script>
import LeftoversInfoDialog from '../dialogs/LeftoversInfoDialog.vue'
import api from '@/api'

export default {
  name: 'LeftoversMobile',

  props: {
    leftovers: {
      type: Array,
      default: () => []
    },
    loaded: {
      type: Boolean,
      default: false
    }
  },

  components: {
    LeftoversInfoDialog
  },

  data: () => ({
    showLeftoversInfoDialog: false,
    activeItem: null
  }),

  computed: {
    isListEmpty () {
      return this.leftovers.length === 0
    },
    totals () {
      if (this.leftovers.length === 0) {
        return {
          groupCount: 0,
          animalCount: 0,
          totalWeight: 0,
          totalLeftovers: 0,
          totalLeftoversPercent: 0,
          avgFeedWeightPerHead: 0,
          avgActualWeightPerHead: 0
        }
      }

      const groupCount = this.leftovers.length
      const animalCount = this.leftovers.reduce((sum, group) => sum + Number(group.animal_count), 0)
      const totalWeight = this.leftovers.reduce((sum, group) => sum + Number(group.fact_weight), 0)
      const totalLeftovers = this.leftovers.reduce((sum, group) => sum + Number(group.fact_leftover), 0)

      const totalLeftoversPercent = this.leftovers.reduce((sum, group) => {
        return sum + Number(this.leftoversPercentage(group))
      }, 0)

      const feedWeights = this.leftovers.map(group => this.feedWeightPerHead(group)).filter(weight => weight > 0)
      const actualWeights = this.leftovers.map(group => this.actualWeightPerHead(group)).filter(weight => weight > 0)

      const avgFeedWeightPerHead = feedWeights.length > 0
        ? feedWeights.reduce((sum, weight) => sum + Number(weight), 0) / feedWeights.length
        : 0

      const avgActualWeightPerHead = actualWeights.length > 0
        ? actualWeights.reduce((sum, weight) => sum + Number(weight), 0) / actualWeights.length
        : 0

      return {
        groupCount,
        animalCount,
        totalWeight,
        totalLeftovers,
        totalLeftoversPercent,
        avgFeedWeightPerHead,
        avgActualWeightPerHead
      }
    }
  },

  methods: {
    leftoversPercentage (group) {
      if (group.animal_count !== 0) {
        return ((group.fact_leftover * 100) / group.fact_weight)
      }
      return 0
    },
    feedWeightPerHead (group) {
      if (group.animal_count !== 0) {
        return (group.fact_weight / group.animal_count)
      }
      return 0
    },
    actualWeightPerHead (group) {
      if (group.animal_count !== 0) {
        return ((group.fact_weight - group.fact_leftover) / group.animal_count)
      }
      return 0
    },
    formatPercentage (value) {
      const num = parseFloat(value)
      return num % 1 === 0 ? num.toString() : num.toFixed(2)
    },
    formatWeight (value) {
      const num = parseFloat(value)
      return num % 1 === 0 ? num.toString() : num.toFixed(2)
    },
    changeFactLeftOver (event, group) {
      let newValue = event.target.value

      if (newValue === '' || newValue == null) {
        newValue = 0
      } else {
        newValue = Number(newValue)
      }
      group.fact_leftover = newValue
      if (group.fact_weight < group.fact_leftover) {
        group.fact_leftover = group.fact_weight
      }
      this.leftoversPercentage(group)
      this.feedWeightPerHead(group)
      this.actualWeightPerHead(group)
      this.updateGroupData(group)
    },
    async updateGroupData (group) {
      const payload = {
        group: {
          id: group.group.id,
        },
        fact_leftover: group.fact_leftover,
        farm: {
          id: group.farm.id,
        },
      }

      await api.leftovers.updateById(group.id, payload)
    },
    openLeftoversInfoDialog (data) {
      this.activeItem = data
      this.showLeftoversInfoDialog = true
    },
    closeLeftoversInfoDialog () {
      this.activeItem = null
      this.showLeftoversInfoDialog = false
    }
  }
}
</script>

<style scoped>
.leftovers-groups {
  max-width: 100%;
  margin: 0 auto;
  padding: 10px;
}

.leftovers-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.group-item {
  background: white;
  border-radius: 10px;
  padding: 15px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

.totals-item {
  background:white;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.group-field {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #e2e2e2;
}

.group-field:last-child {
  border-bottom: none;
}

.group-field-title {
  font-size: 14px;
  color: #666;
}

.group-field-value {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.group-field-value-total {
  font-size: 16px;
  font-weight: 700;
  color: #000;
}

.input {
  width: 80px;
  padding: 5px;
  border: 1px solid #ccc;
  border-radius: 4px;
  text-align: right;
}

.info-icon {
  cursor: pointer;
  margin-left: 5px;
  color: #007bff;
}

.no-data {
  text-align: center;
  padding: 20px;
  color: #666;
}
</style>